import { formatDistanceToNowStrict } from "date-fns";
import { Folder } from "lucide-react";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { AvatarStack } from "~/pages/manifold-nextgen/components/AvatarStack";
import { EditableDescription } from "~/pages/manifold-nextgen/components/EditableDescription";
import { EditableTitle } from "~/pages/manifold-nextgen/components/EditableTitle";
import {
  EntityOverviewHeader,
  OverviewTile,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { PreviewRow } from "~/pages/manifold-nextgen/components/PreviewRow";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { useOwners } from "~/pages/manifold-nextgen/hooks/useOwners";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { ROUTE_NEXTGEN_VIEW, nextGenDatasetDetailPath } from "~/router/constants";
import {
  ProjectAccessLevel,
  projectListPath,
  projectPath,
  updateProject,
} from "~/services/projects";
import { ProjectAccessBadge } from "./ProjectAccessBadge";
import { useProjectContext } from "./useProjectContext";

export const ProjectOverview = () => {
  const { projectId, project, isOwner, attachedAgents, attachedDatasets } = useProjectContext();
  const { collaborators, accessLevels } = useProjectShare(projectId);
  const { showError } = useToast();

  const handleRename = async (next: string) => {
    try {
      await updateProject(projectId, { name: next });
      await Promise.all([mutate(projectPath(projectId)), mutate(projectListPath())]);
    } catch (e) {
      showError("Failed to rename project");
      throw e;
    }
  };

  const handleSaveDescription = async (next: string) => {
    try {
      await updateProject(projectId, { description: next });
      await mutate(projectPath(projectId));
    } catch (e) {
      showError("Failed to update description");
      throw e;
    }
  };

  const owners = useOwners(collaborators, accessLevels, ProjectAccessLevel.OWNER);

  const agents = attachedAgents ?? [];
  const datasets = attachedDatasets ?? [];

  const tiles: OverviewTile[] = [
    {
      label: "My Role",
      content: <ProjectAccessBadge accessLevel={project.access_level} />,
    },
    {
      label: "Owners",
      content: <AvatarStack items={owners} />,
    },
    {
      label: "Last Updated",
      content: formatDistanceToNowStrict(new Date(project.updated_at), {
        addSuffix: true,
      }),
    },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl">
        <EntityOverviewHeader
          tiles={tiles}
          title={
            <EditableTitle
              value={project.name}
              canEdit={isOwner}
              label="project name"
              onSave={handleRename}
            />
          }
        >
          <div className="flex flex-col gap-4">
            {datasets.length > 0 && (
              <div>
                <h2 className="text-sm text-muted-foreground">Datasets ({datasets.length})</h2>
                <PreviewRow
                  className="mt-2"
                  items={datasets.map((d) => ({
                    key: d.id,
                    label: d.title,
                    leading: (
                      <Folder className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    ),
                    to: nextGenDatasetDetailPath(d.id),
                  }))}
                />
              </div>
            )}

            {agents.length > 0 && (
              <div>
                <h2 className="text-sm text-muted-foreground">Agents ({agents.length})</h2>
                <PreviewRow
                  className="mt-2"
                  items={agents.map((a) => ({
                    key: a.id,
                    label: a.display_name,
                    leading: <AgentAvatar agent={a} size={18} />,
                    to: `${ROUTE_NEXTGEN_VIEW}/agents/${a.id}`,
                  }))}
                />
              </div>
            )}

            <EditableDescription
              value={project.description}
              canEdit={isOwner}
              onSave={handleSaveDescription}
            />
          </div>
        </EntityOverviewHeader>
      </div>
    </div>
  );
};
